import React, {useEffect, useMemo, useState, useRef, useCallback} from 'react';
import {Box, Typography} from '@mui/material';
import './TallyLayout.scss';
import '../tally-result/TallyResult.scss';
import '../selection-by/SelectionBy.scss';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import TallyResult from '../tally-result/TallyResult';
import SubTallyResult from '../subTally-result/SubTallyResult';
import ArrSourceSelectedContainer, {
  SourceItem,
} from '../selectSourceContainer/ArrSourceSelectedContainer';
import {
  useGetSubTallyMetadataFilterQuery,
  useGetTallyMetadataFilterQuery,
  useSourceView,
  useSubTallyResult,
  useTallyResult,
} from '../../../../hooks/reporting/useReporting';
import {
  ISourceViewRequestBody,
  ISubtallyDataResponseBody,
  ISubtallyResultResponseBody,
  ITallyDataResponseBody,
  ITallyResultResponseBody,
} from '../../../../api/types/reportingApi.type';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../../redux/store';
import SelectionBy, {SelectionItem} from '../selection-by/SelectionBy';
import {
  buildtagHeirarchy,
  filterSelectionData,
  handleAutoResultUtil,
  handleResultUtil,
  resolveIdFromFixedData,
} from '../../../../utils/reportUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrWaitToCompleteModalWrapper from '../../standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {clearSelectedAction} from '../../../../redux/slice/actionButtonSlice';
import {useLocation} from 'react-router-dom';
import {
  clearActiveActionContext,
  clearMainTally,
  clearPreTallySelects,
  clearSubTally,
  clearTally,
  setPreTallySelects,
} from '../../../../redux/slice/tallyReportSlice';
import {getReportState} from '../../../../api/endpoints/reportingApi';
import {SourceTypeCode} from '../../../../shared/constants';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';

export const payload: ISourceViewRequestBody = {
  reportId: 0,
  reportLabel: 'string',
  source: 'string',
  navigationSourceType: 'string',
  redirectionData: {
    id: 'string',
    text: 'string',
    parent: 'string',
    isGroup: true,
    type: 'string',
    a_attr: 'string',
    isSelected: true,
    state: {disabled: true, selected: true, opened: true},
    li_attr: {isCascadeEnabled: true},
    saveSearchSecurityGroupId: 0,
    documentCount: 0,
    textWithoutCount: 'string',
    searchGroupTypeCode: 'string',
    parentSearchGroupLabel: 'string',
  },
  reportMasterDoclistId: 0,
  pageSource: 'string',
};

type SelectionIdPair = {reportId: number; notificationId: number};
type SelectionIdUpdate = {from: string; values: SelectionIdPair};

const TallyLayout: React.FC = () => {
  const location = useLocation();
  const redirectedAppliedRef = useRef<boolean>(false);

  const {data, refetch} = useSourceView(payload);
  const [isSubtallyDisabled, setIsSubtallyDisabled] = useState(true);
  const [subtallyData, setSubtallyData] = useState<SelectionItem[]>([]);
  const [tallyResult, setTallyResult] = useState<
    boolean | ITallyResultResponseBody
  >(false);
  const [subtallyResult, setSubtallyResult] = useState<
    boolean | ISubtallyResultResponseBody
  >(false);

  const [selectedSourcesBySection, setSelectedSourcesBySection] = useState<
    Record<string, any[] | null>
  >({});

  const [selectionResponse, setSelectionResponse] = useState<
    ITallyDataResponseBody | ISubtallyDataResponseBody
  >();

  const {data: tallyMetadataFilters} = useGetTallyMetadataFilterQuery();
  const {data: subTallyMetadataFilters} = useGetSubTallyMetadataFilterQuery();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tallyBarResult, setTallyBarResult] = useState([]);
  const [lastIdUpdate, setLastIdUpdate] = useState<SelectionIdUpdate | null>(
    null
  );
  const [selectionType, setSelectionType] = useState('');
  const {mutateAsync: fetchTallyResult} = useTallyResult();
  const {mutateAsync: fetchSubtallyResult} = useSubTallyResult();
  const dispatch = useDispatch();
  const [preSelectedSource, setPreSelectedSource] = useState<string[] | null>(
    null
  );

  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );
  const [isPreSelectedData, setIsPreSelectedData] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPreSelectsLoading, setIsPreSelectsLoading] = useState(false);
  const [status, setStatus] = useState<string>('INIT');
  const [preSelectedBars, setPreSelectedBars] = useState<string[]>([]);

  const sources: SourceItem[] = useMemo(() => {
    if (!data) return [];
    const baseSources: SourceItem[] = [
      {
        id: 'securityGroups',
        label: 'Security Groups',
        chipLabel: 'Security Group',
        data: data.tallyInput?.lstSecurityGroups ?? [],
      },
      {
        id: 'searches',
        label: 'Searches',
        chipLabel: 'Search',
        data: data.tallyInput?.searchTreeList ?? [],
      },
      {
        id: 'folders',
        label: 'Folders',
        chipLabel: 'Folder',
        data: data.tallyInput?.folderTreeList ?? [],
      },
      {
        id: 'tags',
        label: t('reports.tally.source.tags'),
        chipLabel: t('reports.tally.source.tagChip'),
        data: data.tallyInput?.lstTagsList ?? [],
      },
    ];
    const conditionalItem =
      userRole === 'Review Manager'
        ? {
            id: 'assignments',
            label: 'Assignments',
            chipLabel: 'Assignment',
            data: data.tallyInput?.assignmentTreeList ?? [],
          }
        : {
            id: 'projects',
            label: 'Projects',
            chipLabel: 'Project',
            data: data.tallyInput?.lstProjects ?? [],
          };

    baseSources.splice(2, 0, conditionalItem);
    return baseSources;
  }, [data, userRole]);

  const tallyData: SelectionItem[] = useMemo(() => {
    if (!data) return [];
    return [
      {
        id: 'Metadata',
        label: 'Metadata',
        type: 'single',
        key: ['MD'],
        data:
          buildtagHeirarchy(data.tallyInput?.lstMetadata, {
            idKey: 'fieldId',
            textKey: 'fieldLabel',
            unqKey: 'MD',
          }) ?? [],
      },
      {
        id: 'tags',
        label: 'Tags',
        type: 'multi',
        key: ['TG'],
        data:
          buildtagHeirarchy(data.tallyInput?.lstTagsList, {
            idKey: 'id',
            textKey: 'text',
            unqKey: 'TG',
          }) ?? [],
      },
      {
        id: 'tagclassifications',
        label: 'Tag Classifications',
        type: 'multi',
        key: ['TC'],
        data:
          buildtagHeirarchy(data.tallyInput?.lstTagClassification, {
            idKey: 'tagClassificationId',
            textKey: 'tagClassificationName',
            unqKey: 'TC',
          }) ?? [],
      },
      {
        id: 'redactiontags',
        label: 'Redaction Tags',
        type: 'multi',
        key: ['RT'],
        data:
          buildtagHeirarchy(data.tallyInput?.lstRedactionTagsList, {
            idKey: 'id',
            textKey: 'text',
            unqKey: 'RT',
          }) ?? [],
      },
      {
        id: 'folders',
        label: 'Folders',
        type: 'multi',
        key: ['FD'],
        data:
          buildtagHeirarchy(data.tallyInput?.folderTreeList, {
            idKey: 'id',
            textKey: 'text',
            unqKey: 'FD',
          }) ?? [],
      },
      {
        id: 'assignments',
        label: 'Assignments',
        type: 'multi',
        key: ['AG'],
        data:
          buildtagHeirarchy(data.assignmentList, {
            idKey: 'id',
            textKey: 'text',
            unqKey: 'AG',
          }) ?? [],
      },
    ].filter((item) => item.data.length > 0);
  }, [data]);

  const {isBackgroundReport, reportId} = location.state ?? {};

  const urlReportId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get('ReportID');
    return id ? Number(id) : null;
  }, [location.search]);

  const preReportId = isBackgroundReport ? reportId : urlReportId;

  const hardReset = useCallback(() => {
    dispatch(clearMainTally());
    dispatch(clearTally());
    dispatch(clearSubTally());
    dispatch(clearSelectedAction());
    dispatch(clearActiveActionContext());
    dispatch(clearPreTallySelects());
    setIsPreSelectedData(null);
    setTallyResult(false);
    setSubtallyResult(false);
    setSubtallyData([]);
    setIsSubtallyDisabled(true);
    setPreSelectedSource(null);
    setSelectionResponse(undefined);
    setSelectionType('');
    setTallyBarResult([]);
  }, [dispatch]);

  useEffect(() => {
    hardReset();
  }, []);

  const handleIdUpdate = (from: string, values: SelectionIdPair) => {
    setLastIdUpdate({from, values});
  };

  const getSelectionIds = (
    selectionType: string,
    response: ITallyDataResponseBody | ISubtallyDataResponseBody
  ) => {
    const idSource =
      selectionType === 'tally' && 'backgroundModelData' in response
        ? response.backgroundModelData
        : 'shareReportData' in response
          ? response.shareReportData
          : undefined;

    if (idSource?.reportID && idSource?.notificationID) {
      handleIdUpdate(selectionType, {
        reportId: idSource.reportID,
        notificationId: idSource.notificationID,
      });
    }
  };

  const currentIds = useMemo<SelectionIdPair | null>(() => {
    return lastIdUpdate ? lastIdUpdate.values : null;
  }, [lastIdUpdate]);

  const isSourceNotSelected: boolean = useMemo(() => {
    return Object.values(selectedSourcesBySection).every((arr) => {
      if (arr == null) return true;
      if (Array.isArray(arr)) return arr.length === 0;
      return true;
    });
  }, [selectedSourcesBySection]);

  useEffect(() => {
    if (isSourceNotSelected) {
      setStatus('EDIT');
      hardReset();
    }
  }, [isSourceNotSelected]);

  const [showLoaderOverlay, setShowLoaderOverlay] = useState(false);
  const [showWaitModal, setShowWaitModal] = useState(false);
  const waitTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const hasRedirectState = !!location?.state;
    const hasReportIdQP = !!new URLSearchParams(window.location.search).get(
      'ReportID'
    );

    if (!hasRedirectState && !isBackgroundReport && !hasReportIdQP) {
      setIsSubtallyDisabled(true);
      dispatch(clearMainTally());
    }
  }, []);

  const handleStartLoading = () => setShowLoaderOverlay(true);

  const handleLoadingUpdate = (value: boolean) => setShowLoaderOverlay(value);

  const handleStopLoading = () => {
    setShowLoaderOverlay(false);
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
  };

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    const state: any = location?.state as any;

    const hasCommMap =
      !!state &&
      state.navigationSourceType === 'DOCLIST' &&
      state.sourceName === 'COMMUNICATIONMAP' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasConceptExplorer =
      !!state &&
      state.navigationSourceType === 'CONCEPTEXPLORER' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasTimeline =
      !!state &&
      state.navigationSourceType === 'TIMELINE' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasIngestion =
      !!state &&
      state.navigationSourceType === 'INGESTION' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasAdvBatchMgt =
      !!state &&
      state.navigationSourceType === 'ADVBATMGMTRPT' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasSessionSearch =
      !!state &&
      state.navigationSourceType === 'SESSIONSEARCH' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasSavedSearch =
      !!state &&
      state.navigationSourceType === 'SEARCH' &&
      state.sourceName === 'SEARCHES' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasDocList =
      !!state &&
      state.navigationSourceType === 'DOCLIST' &&
      state.sourceName === 'DOCLIST' &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    const hasBulkTag =
      !!state &&
      state.navigationSourceType === SourceTypeCode.BULKTAG &&
      state.sourceName === SourceTypeCode.BULKTAG &&
      Array.isArray(state.source) &&
      state.source.length > 0;

    if (
      (hasCommMap ||
        hasConceptExplorer ||
        hasTimeline ||
        hasIngestion ||
        hasAdvBatchMgt ||
        hasSessionSearch ||
        hasSavedSearch ||
        hasDocList ||
        hasBulkTag) &&
      !redirectedAppliedRef.current
    ) {
      redirectedAppliedRef.current = true;

      if (hasSavedSearch) {
        const savedName = state.savedSearchName ?? '';
        setSelectedSourcesBySection({
          searches: [
            {
              navigationSourceType: state.navigationSourceType,
              source: state.source,
              sourceName: state.sourceName,
              labels: `Search: Selected Documents from SavedSearch: ${savedName}`,
            },
          ],
        });
        return;
      }

      setSelectedSourcesBySection({
        documents: [
          {
            navigationSourceType: state.navigationSourceType,
            source: state.source,
            sourceName: state.sourceName,
            labels: hasCommMap
              ? 'Documents: Selected Documents from Communication Map'
              : hasConceptExplorer
                ? 'Documents: Selected Documents from Concept Explorer'
                : hasTimeline
                  ? 'Documents: Selected Documents from Timeline'
                  : hasIngestion
                    ? 'Documents: Selected Documents from Ingestion'
                    : hasAdvBatchMgt
                      ? 'Documents: Selected Documents from AdvBatchMangementRpt'
                      : hasSessionSearch
                        ? 'Documents: Selected Documents from Search'
                        : hasDocList
                          ? 'Documents: Selected Documents from Doc List'
                          : hasBulkTag
                            ? t('reports.tally.sourceContainer.bulkTagAction')
                            : 'Documents: Selected Documents from Search',
          },
        ],
      });
    }
  }, [location?.state]);

  useEffect(() => {
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
    waitTimerRef.current = setTimeout(() => {
      if (showLoaderOverlay) setShowWaitModal(true);
    }, 1000);
  }, [showLoaderOverlay]);

  useEffect(() => {
    if (tallyResult === false) {
      dispatch(clearSubTally());
      setSubtallyData([]);
      setIsSubtallyDisabled(true);
    }
  }, [tallyResult]);

  const actionMenuSelected = useSelector(
    (state: RootState) => state.actionButton.selectedActionMenu
  );

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  useEffect(() => {
    setIsPreSelectedData(null);
    setIsPreSelectsLoading(!!preReportId);
    if (preReportId && tallyData) {
      let cancelled = false;
      handleStartLoading();
      (async () => {
        try {
          const response = await getReportState(preReportId);
          if (cancelled) return;
          dispatch(setPreTallySelects(response));

          setStatus('PREP');

          const tallyFilterResponse = response?.tallyFilter ?? null;

          if (!tallyFilterResponse) return;

          const sourceIDs = tallyFilterResponse.sourceID;
          const normalized =
            Array.isArray(sourceIDs) &&
            tallyFilterResponse.sourceType !== SourceTypeCode.BULKTAG
              ? sourceIDs.map(String)
              : null;

          setPreSelectedSource(normalized);

          const tallyfilterData = tallyFilterResponse.tally;
          const key = tallyfilterData.tallyByType;

          const keyid =
            tallyfilterData.tallyByID == '' || !tallyfilterData.tallyByID
              ? tallyfilterData.tallyByIDExceptMetadata
              : tallyfilterData.tallyByID;

          const tallyId = {key: key, id: keyid};

          const getPreTallySelectId = resolveIdFromFixedData(
            tallyData,
            tallyId
          );

          if (!getPreTallySelectId) return;

          const preTallyIds = getPreTallySelectId.formatted;

          const subTallySource = filterSelectionData(tallyData, [
            ...preTallyIds,
          ]);

          const preBarSelections =
            tallyfilterData.filterBySelection.length > 0 &&
            tallyfilterData?.filterBySelection[0]?.literalValue.match(
              /N'([^']*)'/g
            )
              ? tallyfilterData?.filterBySelection[0]?.literalValue
                  .match(/N'([^']*)'/g)
                  .map((s: any) => s.slice(2, -1))
              : [];

          setPreSelectedBars(preBarSelections);
          setSubtallyData(subTallySource ?? []);

          setIsPreSelectedData(response);

          if (tallyfilterData?.tallyByCriteriaList) {
            await delay(1000);
            setTallyResult({
              tallyByCriteriaList: tallyfilterData.tallyByCriteriaList,
              isDocCountView: true,
              preSelected: preBarSelections ?? [],
            });
          }
          await delay(1000);
          if (tallyFilterResponse.subTally.tallyByType) {
            setIsSubtallyDisabled(false);
            handleAutoApply(response);
          }
        } finally {
          if (!cancelled)
            setTimeout(() => {
              setIsPreSelectsLoading(false);
              handleStopLoading();
            }, 0);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [preReportId, tallyData]);

  useEffect(() => {
    const action = String(actionMenuSelected?.value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '');
    if (action === 'subtally') setIsSubtallyDisabled(false);
  }, [actionMenuSelected]);

  useEffect(() => {
    if (!showWaitModal) setShowLoaderOverlay(false);
  }, [showWaitModal]);

  const handleResultApply = useCallback(
    async (reportId: number) => {
      const isTally = selectionType === 'tally';

      if (!selectionResponse) {
        (isTally ? setTallyResult : setSubtallyResult)(false);
        handleStopLoading();
        return;
      }

      const repId =
        isTally && 'backgroundModelData' in selectionResponse
          ? selectionResponse.backgroundModelData.reportID
          : 'shareReportData' in selectionResponse
            ? selectionResponse.shareReportData.reportID
            : undefined;

      if (reportId !== repId) return;

      try {
        const {success, data} = await handleResultUtil({
          applyType: selectionType,
          fetchTallyResult,
          fetchSubtallyResult,
          reportResponse: selectionResponse,
        });

        if (success && data) {
          if (isTally) setTallyResult(data as ITallyResultResponseBody);
          else setSubtallyResult(data as ISubtallyResultResponseBody);
        } else {
          (isTally ? setTallyResult : setSubtallyResult)(false);
        }
      } finally {
        handleStopLoading();
      }
    },
    [selectionResponse, selectionType, fetchTallyResult, fetchSubtallyResult]
  );

  const handleAutoApply = async (preSelects: any) => {
    if (!preSelects) return;
    handleStartLoading();
    try {
      const {success, data} = await handleAutoResultUtil({
        fetchSubtallyResult,
        preSelects,
      });

      if (success && data) {
        setSubtallyResult(data as ISubtallyResultResponseBody);
      } else {
        setSubtallyResult(false);
      }
    } finally {
      handleStopLoading();
    }
  };

  return (
    <Box className="tally-container">
      <ArrLoadingSpinner
        show={showLoaderOverlay || !data}
        overlay={true}
        isOpen={true}
      />
      {showWaitModal && currentIds && (
        <ArrWaitToCompleteModalWrapper
          key={`${currentIds?.notificationId}-${currentIds?.reportId}`}
          notificationId={currentIds.notificationId}
          reportId={currentIds.reportId}
          onComplete={(reportId) => {
            handleStopLoading();
            handleResultApply(reportId);
          }}
          onFlag={(value) => setShowLoaderOverlay(!value)}
          loader={false}
          showClose={false}
        />
      )}
      <div className="tally-header" data-testid={`tally-layout-wrapper`}>
        <Typography
          className="typo-text headline2_size"
          aria-label={t('reports.tally.title')}
          role="header"
          tabIndex={0}
          data-testid={`tally-layout-title`}
        >
          {t('reports.tally.title')}
        </Typography>
        <ArrTooltipWrapper
          data-testid="ceh-tooltip-wrapper"
          title={t('reports.tally.title')}
          closeTooltipOnEscPress={true}
          trigger={
            <div tabIndex={0} data-testid={`tally-layout-info-div`}>
              <InfoIcon
                data-testid="info-icon"
                className="info-icon"
                aria-label={'info-icon'}
                tabIndex={0}
              />
            </div>
          }
        >
          <div data-testid="ceh-tooltip-content" tabIndex={0}>
            <p data-testid={`tally-layout-top-content`}>
              {t('reports.tally.tooltip.topContent')}
            </p>
            <p data-testid={`tally-layout-tally-content`}>
              {t('reports.tally.tooltip.tallyContent')}
            </p>
            <ul data-testid={`tally-layout-ul`}>
              {(
                t('reports.tally.tooltip.tallyPoints', {
                  returnObjects: true,
                }) as string[]
              ).map((point, idx) => (
                <li
                  key={idx}
                  data-testid={`tally-layout-li-tally-points-${idx}`}
                >
                  {point}
                </li>
              ))}
            </ul>
            <p data-testid={`tally-layout-sub-tally-content`}>
              {t('reports.tally.tooltip.subTallyContent')}
            </p>
            <ul data-testid={`tally-layout-ul-btn`}>
              {(
                t('reports.tally.tooltip.subTallyPoints', {
                  returnObjects: true,
                }) as string[]
              ).map((point, idx) => (
                <li
                  key={idx}
                  data-testid={`tally-layout-li-subtally-points-${idx}`}
                >
                  {point}
                </li>
              ))}
            </ul>
            <p data-testid={`tally-layout-export-content`}>
              {t('reports.tally.tooltip.exportContent')}
            </p>
          </div>
        </ArrTooltipWrapper>
      </div>
      <Box
        data-testid="tally-top-panel"
        className="tally-top-panel"
        tabIndex={0}
      >
        <ArrSourceSelectedContainer
          key={`tss-${preReportId}`}
          sources={sources}
          onSelectionsUpdate={setSelectedSourcesBySection}
          preSelectedSource={preSelectedSource}
        />
      </Box>
      <Box data-testid="tally-middle-section" className="tally-middle-section">
        <Box
          data-testid="tally-column"
          className="tally-column column-border-right"
        >
          <Box
            data-testid="tally-filter-panel"
            className="tally-filter-panel"
            tabIndex={0}
          >
            <SelectionBy
              key={`tsb-${preReportId ?? status}`}
              title={t('reports.tally.selectionBy.tallyBy')}
              disableButton={isSourceNotSelected}
              titleAlwaysEnabled={true}
              btnLabel={t('reports.tally.selectionBy.tallybtnLabel')}
              filterData={tallyMetadataFilters}
              sourceData={selectedSourcesBySection}
              selectionData={tallyData}
              selectionChanged={(type) =>
                (type === 'tally' ? setTallyResult : setSubtallyResult)(false)
              }
              selectionResponse={(type, response) => {
                setSelectionResponse(response);
                setSelectionType(type);
                getSelectionIds(type, response);
              }}
              setSubSelectionData={(value: SelectionItem[]) =>
                setSubtallyData(value)
              }
              onStartLoading={handleStartLoading}
              onStopLoading={handleStopLoading}
              preSelectedData={isSourceNotSelected ? null : isPreSelectedData}
            />
          </Box>

          <TallyResult
            key={`tr-${preReportId}`}
            isTallyRun={tallyResult}
            handleLoading={handleLoadingUpdate}
            barResult={(value: any) => setTallyBarResult(value)}
            preSelectedBars={preSelectedBars}
          />
        </Box>

        <Box
          data-testid="tally-column"
          className="tally-column column-border-left"
        >
          <Box
            data-testid="sub-tally-filter-panel"
            className="sub-tally-filter-panel"
            tabIndex={0}
          >
            <SelectionBy
              key={`ssb-${preReportId ?? status}-${isSourceNotSelected}`}
              title={t('reports.tally.selectionBy.subtallyBy')}
              disabled={isSubtallyDisabled}
              btnLabel={t('reports.tally.selectionBy.subtallybtnLabel')}
              filterData={subTallyMetadataFilters}
              sourceData={selectedSourcesBySection}
              selectionData={subtallyData}
              selectionChanged={(type) =>
                (type === 'tally' ? setTallyResult : setSubtallyResult)(false)
              }
              selectionResponse={(type, response) => {
                setSelectionResponse(response);
                setSelectionType(type);
                getSelectionIds(type, response);
              }}
              setSubSelectionData={() => setSubtallyData([])}
              onStartLoading={handleStartLoading}
              onStopLoading={handleStopLoading}
              preSelectedData={isSubtallyDisabled ? null : isPreSelectedData}
            />
          </Box>

          {isSubtallyDisabled ? (
            <Box data-testid="tally-panel" className="tally-panel">
              <Typography
                className="typo-text subhead5_size unavailable-text"
                data-testid={`tally-layout-subtallydisable`}
                tabIndex={0}
              >
                {t('reports.tally.selectionBy.subtallydisable')}
              </Typography>
            </Box>
          ) : (
            <Box
              data-testid="subtally-empty-panel"
              className="subtally-empty-panel"
              tabIndex={0}
            >
              <SubTallyResult
                key={`sr-${preReportId}`}
                isSubTallyRun={subtallyResult}
                handleLoading={handleLoadingUpdate}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TallyLayout;
